/**
 * Der Fahrplan, wie ihn die App zeigt.
 *
 * `docs/ROADMAP.md` bleibt der geschriebene Plan -- dort steht das Warum, dort
 * schreibe ich weiter, und die Datei fährt im Image mit. Was sich *in der App*
 * ändern soll, ist etwas anderes: die Reihenfolge, die Sascha per Zug
 * festlegt, und die Punkte, die über den Feedback-Knopf dazukommen. Das steht
 * in der Datenbank.
 *
 * Beides trifft sich über den Schlüssel eines Punktes ("7.5b", "0.3a"): der
 * Abgleich beim Start legt neue Punkte an, bringt geänderten Text nach und
 * lässt jede Reihenfolge in Ruhe, die jemand gesetzt hat.
 */
const HEADING = /^##\s+(.+)$/;
/**
 * "- [ ] **7.5b Titel** Rest" oder eingerückt darunter dasselbe. Der Schlüssel
 * ist entweder eine Nummer aus dem Fahrplan ("7.5b", "0.3a") oder ein Kürzel
 * aus der Vorschlagsliste ("S12").
 */
const ITEM = /^(\s*)-\s+\[([ xX])\]\s+\*\*([A-Za-z]{0,2}[0-9]+(?:\.[0-9]+)?[a-z]?)\s+(.*)$/;
/**
 * **Ein Titel darf umbrechen** (7.165). Bis zum 25. September musste der
 * fette Titel auf derselben Zeile schliessen. Tat er es nicht, fehlte der
 * Punkt in der App -- 7.85, 7.118 und 7.143 standen nie in Saschas
 * Reihenfolge --, und sein Text hing am Punkt davor. Jetzt wird weitergelesen,
 * bis die fetten Sterne schliessen, höchstens `TITEL_ZEILEN` Zeilen weit.
 */
const TITEL_ZEILEN = 6;
/**
 * Fasst Zeilen zu einem Absatz zusammen und wirft die Einrückung weg. Ein
 * Gedankenstrich oder Doppelpunkt am Anfang gehört zum Satzbau der Datei
 * ("**Titel** — Erklärung") und nicht zum Text; in einer Karte steht er sonst
 * ohne Bezug herum.
 */
function tidy(text) {
    return text
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .join(' ')
        .replace(/\s+/g, ' ')
        .replace(/^[—–:-]\s*/, '')
        .trim();
}
export function parseRoadmap(markdown) {
    const entries = [];
    let phase = '';
    let current = null;
    let buffer = '';
    const flush = () => {
        if (!current)
            return;
        current.detail = tidy(buffer);
        entries.push(current);
        current = null;
        buffer = '';
    };
    const zeilen = markdown.split('\n');
    for (let i = 0; i < zeilen.length; i++) {
        const raw = zeilen[i];
        const heading = HEADING.exec(raw.trim());
        if (heading) {
            flush();
            phase = heading[1].trim();
            continue;
        }
        const item = ITEM.exec(raw);
        if (item) {
            // Der Titel läuft bis zu den schliessenden Sternen, notfalls über
            // Zeilen hinweg.
            let rest = item[4];
            let ende = rest.indexOf('**');
            let j = i;
            while (ende < 0 && j + 1 < zeilen.length && j - i < TITEL_ZEILEN && !ITEM.exec(zeilen[j + 1]) && !HEADING.exec(zeilen[j + 1].trim())) {
                j++;
                rest += ` ${zeilen[j].trim()}`;
                ende = rest.indexOf('**');
            }
            if (ende < 0) {
                // Nie geschlossen: dann bleibt es bei der ersten Zeile als Titel.
                rest = item[4];
                j = i;
            }
            flush();
            const titel = ende < 0 ? rest : rest.slice(0, ende);
            current = {
                key: item[3],
                // Ein Titel endet oft auf einem Punkt, weil der Satz weiterläuft.
                title: titel.replace(/\s+/g, ' ').trim().replace(/[.:]$/, ''),
                detail: '',
                done: item[2].toLowerCase() === 'x',
                phase,
                depth: Math.floor(item[1].length / 6),
                order: entries.length,
            };
            buffer = ende < 0 ? '' : rest.slice(ende + 2);
            i = j;
            continue;
        }
        // Alles, was weder Überschrift noch neuer Punkt ist, gehört zum laufenden.
        if (current)
            buffer += `\n${raw}`;
    }
    flush();
    return entries.map((e, i) => ({ ...e, order: i }));
}
/**
 * Dringendes zuerst, sonst bleibt die Reihenfolge (7.75).
 *
 * Sascha, 8. September: „wir haben so viele Dringende in der Roadmap. Sollten
 * sie nicht an Pos 1 rutschen?“ Der Server liefert die Liste schon so; hier
 * steht dieselbe Regel für die App, damit ein Haken bei „dringend“ den Punkt
 * sofort hebt und nicht erst nach dem Neuladen. Stabil: unter den Dringenden
 * und unter den anderen bleibt der Rang, wie er war.
 */
export function dringendZuerst(items) {
    return [...items].sort((a, b) => Number(b.urgent) - Number(a.urgent));
}
//# sourceMappingURL=roadmap.js.map